import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { User as FirebaseUser } from 'firebase/auth';
import { TokenManager } from '../services/api';
import PhoneLoginModal from './PhoneLoginModal';
import LoginModal from './LoginModal';

interface EnhancedLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  forceLogin?: boolean;
}

type LoginState = 'phone' | 'name' | 'closed';

const EnhancedLoginModal: React.FC<EnhancedLoginModalProps> = ({
  isOpen,
  onClose,
  forceLogin = false
}) => {
  const [loginState, setLoginState] = useState<LoginState>('phone');
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const { login, loginWithFirebaseVerification } = useAuth();

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
      setLoginState('phone');
      setFirebaseUser(null);
    } else {
      setLoginState('closed');
    }
  }, [isOpen]);

  const handleFirebaseSuccess = (user: FirebaseUser, isExistingUser?: boolean) => {
    setFirebaseUser(user);
    
    if (isExistingUser) {
      // User đã tồn tại và đã login thành công, đóng modal
      setLoginState('closed');
      onClose();
    } else {
      // User mới, chuyển sang step nhập tên
      setLoginState('name');
    }
  };

  const handleNameLogin = async (name: string, phone: string, address?: string): Promise<boolean> => {
    let success = false;

    if (firebaseUser) {
      // Login with Firebase verification
      success = await loginWithFirebaseVerification(name, firebaseUser, address);
    } else {
      // Regular login without Firebase verification
      success = await login(name, phone, address);
    }

    if (success) {
      setLoginState('closed');
      onClose();
    }

    return success;
  };

  const handlePhoneModalClose = () => {
    if (forceLogin) {
      // If forced login, redirect to name login
      setLoginState('name');
    } else {
      setLoginState('closed');
      onClose();
    }
  };

  const handleNameModalClose = () => {
    if (forceLogin) {
      // If forced login, go back to phone step
      setLoginState('phone');
    } else {
      setLoginState('closed');
      onClose();
    }
  };

  if (!isOpen || loginState === 'closed') {
    return null;
  }

  return (
    <>
      {/* Phone verification step */}
      <PhoneLoginModal
        isOpen={loginState === 'phone'}
        onClose={handlePhoneModalClose}
        onFirebaseSuccess={handleFirebaseSuccess}
        forceLogin={forceLogin}
      />

      {/* Name input step */}
      <LoginModal
        isOpen={loginState === 'name'}
        onClose={handleNameModalClose}
        onLogin={handleNameLogin}
        forceLogin={forceLogin}
        firebaseUser={firebaseUser}
        showBackButton={!forceLogin}
        onBack={() => setLoginState('phone')}
      />
    </>
  );
};

export default EnhancedLoginModal;
